import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  Blocks,
  Bot,
  CalendarClock,
  ChevronDown,
  ChevronsUpDown,
  Database,
  Gift,
  Globe,
  HelpCircle,
  Loader2,
  LogOut,
  MessagesSquare,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
  UsersRound,
  Wallet,
  Wand2,
  type LucideIcon,
} from "lucide-react";

import {
  getBillingPlans,
  getMe,
  logout,
  redeemCode,
  startCheckout,
  switchWorkspace,
  type CatalogPlan,
  type CreditTier,
  type Me,
} from "@/lib/api";
import { humanizeError } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/Page";

const POPULAR = "pro"; // the tier we highlight

// InfoTip is a small "?" affordance with a hover/focus tooltip (no UI-kit
// tooltip primitive exists). Keyboard-accessible via the focusable button.
function InfoTip({ text }: { text: string }) {
  return (
    <span className="group/tip relative inline-flex align-middle">
      <button
        type="button"
        aria-label={text}
        className="text-muted-foreground/50 transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-none"
      >
        <HelpCircle className="size-3.5" />
      </button>
      <span
        role="tooltip"
        className="pointer-events-none absolute bottom-full right-0 z-20 mb-2 w-52 rounded-md border bg-popover px-3 py-2 text-left text-xs font-normal leading-snug text-popover-foreground opacity-0 shadow-md transition-opacity duration-150 group-hover/tip:opacity-100 group-focus-within/tip:opacity-100"
      >
        {text}
      </span>
    </span>
  );
}

const DAILY_TIP =
  "On top of your monthly pool, your balance tops back up by this many credits every day (never above your plan's cap) — so a heavy day can't drain you to zero.";
const MEMORY_TIP =
  "Each agent keeps its own private workspace — files, notes, and long-term memory that persist between runs, so it remembers context and builds on past work.";

const euro = (cents: number) =>
  `€${(cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: cents % 100 ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;

const count = (v: number, unit: string) =>
  v === -1 ? `Unlimited ${unit}s` : `${v.toLocaleString()} ${unit}${v === 1 ? "" : "s"}`;

// integrationsLine merges integration KINDS + COUNT into one non-technical line
// ("connected integrations", not "connected servers").
function integrationsLine(p: CatalogPlan): string {
  const kinds = p.stdio_openapi_integrations ? "MCP, HTTP API & stdio/npx" : "MCP";
  const n = p.connected_servers === -1 ? "Unlimited" : p.connected_servers;
  return `${n} connected integrations (${kinds})`;
}
function schedulingLine(p: CatalogPlan): string {
  return p.scheduled_tasks === -1
    ? "Unlimited scheduled & recurring tasks"
    : `${p.scheduled_tasks}× scheduled & recurring tasks`;
}

type Feature = { icon: LucideIcon; label: string; tip?: string };

// planFeatures is the per-card list (icon + label), data-driven from the plan's
// entitlements. We deliberately don't surface API keys (being removed) or SSO
// (not built yet). Monthly credits live in the selector above; the daily refresh
// leads the list (with its tip).
function planFeatures(p: CatalogPlan): Feature[] {
  const f: Feature[] = [];
  if (p.trial_days > 0) f.push({ icon: Gift, label: `${p.trial_days}-day free trial` });
  f.push(
    { icon: RefreshCw, label: `+${p.daily_refresh_credits.toLocaleString()} credits / day`, tip: DAILY_TIP },
    { icon: Bot, label: "Autonomous AI agents" },
    { icon: MessagesSquare, label: "Chat, Telegram & Slack channels" },
    { icon: Wand2, label: "Build agents with AI" },
    { icon: Database, label: "Per-agent memory & storage", tip: MEMORY_TIP },
    { icon: Users, label: count(p.seats, "seat") },
    { icon: Blocks, label: integrationsLine(p) },
  );
  if (p.scheduling) f.push({ icon: CalendarClock, label: schedulingLine(p) });
  if (p.concurrent_missions !== 0)
    f.push({
      icon: Target,
      label:
        p.concurrent_missions === -1
          ? "Unlimited autonomous missions"
          : `${p.concurrent_missions} concurrent autonomous mission${p.concurrent_missions === 1 ? "" : "s"}`,
    });
  if (p.custom_domain) f.push({ icon: Globe, label: "Custom domains" });
  if (p.per_agent_budgets) f.push({ icon: Wallet, label: "Per-agent budget caps" });
  if (p.policy_enforce) f.push({ icon: ShieldCheck, label: "Policy enforcement & approvals" });
  if (p.advanced_rbac) f.push({ icon: UsersRound, label: "Advanced per-project roles" });
  return f;
}

function PlanCard({
  plan,
  annual,
  busy,
  canBuy,
  onSubscribe,
}: {
  plan: CatalogPlan;
  annual: boolean;
  busy: boolean;
  canBuy: boolean;
  onSubscribe: (id: string, credits: number, interval: "month" | "year") => void;
}) {
  const popular = plan.id === POPULAR;
  const tiers = plan.tiers ?? [];
  const [tierIdx, setTierIdx] = useState(0);
  const tier: CreditTier | undefined = tiers[tierIdx];
  const credits = tier ? tier.credits : plan.monthly_credits;
  const annualTotalCents = tier ? tier.annual_price_eur_cents : 0;
  // Price shown is always "per month"; on annual we show the discounted effective
  // monthly (annual total ÷ 12) plus the billed-yearly total underneath.
  const monthlyCents = tier ? tier.price_eur_cents : plan.price_eur_cents;
  const shownMonthlyCents = annual ? Math.round(annualTotalCents / 12) : monthlyCents;
  const purchasable = annual
    ? tier
      ? tier.annual_purchasable
      : plan.annual_purchasable
    : tier
      ? tier.purchasable
      : plan.purchasable;
  const trial = plan.trial_days > 0;
  const features = planFeatures(plan);

  return (
    <Card
      className={
        "relative flex flex-col " +
        (popular ? "border-primary shadow-lg ring-1 ring-primary/40" : "")
      }
    >
      {popular && (
        <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2 gap-1">
          <Sparkles className="size-3" /> Most popular
        </Badge>
      )}
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold text-muted-foreground">{plan.name}</CardTitle>
        <div className="mt-0.5 flex items-baseline gap-1">
          <span className="text-3xl font-semibold tracking-tight text-foreground">{euro(shownMonthlyCents)}</span>
          <span className="text-sm text-muted-foreground">/ month</span>
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {annual ? `billed annually · ${euro(annualTotalCents)}/yr` : "billed monthly"}
        </p>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4">
        {/* CTA on top */}
        <Button
          size="lg"
          className="w-full rounded-full font-semibold"
          variant={popular ? "default" : "secondary"}
          disabled={!purchasable || !canBuy || busy}
          onClick={() => onSubscribe(plan.id, credits, annual ? "year" : "month")}
        >
          {busy ? <Spinner /> : null}
          {!purchasable ? "Unavailable" : trial ? "Get started for free" : "Subscribe"}
        </Button>

        {/* Credit-tier selector pill */}
        {tiers.length > 1 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex w-full items-center justify-between rounded-lg bg-muted px-3.5 py-2.5 text-sm font-medium transition-colors hover:bg-muted/70">
                <span>{credits.toLocaleString()} credits / month</span>
                <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)]">
              {tiers.map((t, i) => (
                <DropdownMenuItem key={t.credits} onSelect={() => setTierIdx(i)}>
                  {t.credits.toLocaleString()} credits / month
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <div className="flex w-full items-center rounded-lg bg-muted px-3.5 py-2.5 text-sm font-medium">
            {credits.toLocaleString()} credits / month
          </div>
        )}

        {/* Feature list with per-feature icons */}
        <ul className="flex-1 space-y-2.5 text-sm">
          {features.map((f) => (
            <li key={f.label} className="flex items-start gap-2.5">
              <f.icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <span className="flex items-center gap-1">
                {f.label}
                {f.tip && <InfoTip text={f.tip} />}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

// EnterpriseBanner is the wide, full-width row beneath the three plans.
function EnterpriseBanner() {
  return (
    <div className="mt-6 flex flex-col items-start justify-between gap-4 rounded-xl border bg-card p-6 sm:flex-row sm:items-center">
      <div>
        <h3 className="text-lg font-semibold">Enterprise</h3>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Custom credit volume, unlimited seats &amp; integrations, advanced security, and dedicated
          support &amp; onboarding — tailored to your organization.
        </p>
      </div>
      <Button asChild variant="outline" className="shrink-0">
        <a href="mailto:sales@codebridge.ffmux.com?subject=CodeBridge%20Enterprise">
          Contact sales <ArrowRight className="size-4" />
        </a>
      </Button>
    </div>
  );
}

export function Onboarding({
  me,
  onActivated,
  onSignedOut,
}: {
  me: Me;
  onActivated: () => void;
  onSignedOut: () => void;
}) {
  const [plans, setPlans] = useState<CatalogPlan[] | null>(null);
  const [err, setErr] = useState("");
  const [busyPlan, setBusyPlan] = useState("");
  const [cycle, setCycle] = useState<"month" | "year">("month"); // billing interval (not window.setInterval!)
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [redeemErr, setRedeemErr] = useState("");
  const [redeemOpen, setRedeemOpen] = useState(false);

  // Returned from Stripe Checkout: success → poll until the webhook flips the
  // workspace to active; cancelled → just show the picker with a note.
  const params = new URLSearchParams(window.location.search);
  const returned = params.get("checkout"); // "success" | "cancelled" | null
  const activating = returned === "success";

  const current = me.workspaces?.find((w) => w.tenant_id === me.current_tenant_id);
  const others = (me.workspaces ?? []).filter((w) => w.tenant_id !== me.current_tenant_id);
  const isOwner = current?.role === "owner"; // only the owner can manage billing

  useEffect(() => {
    getBillingPlans()
      .then((r) => setPlans(r.plans))
      .catch((e) => setErr(humanizeError(e, "We couldn't load the plans. Please refresh.")));
  }, []);

  // Poll /me while activating: the Stripe webhook assigns the plan + flips the
  // workspace to active asynchronously, so we wait for that to land, then hand
  // control back to the app. Give up after ~40s with a manual refresh fallback.
  // onActivated is held in a ref so the parent recreating its inline callback
  // each render doesn't churn the interval (resetting the counter/timeout).
  const [timedOut, setTimedOut] = useState(false);
  const onActivatedRef = useRef(onActivated);
  useEffect(() => {
    onActivatedRef.current = onActivated;
  });
  useEffect(() => {
    if (!activating) return;
    let n = 0;
    const id = setInterval(async () => {
      n++;
      try {
        const fresh = await getMe();
        const cur = fresh.workspaces?.find((w) => w.tenant_id === fresh.current_tenant_id);
        if (cur && cur.status !== "pending") {
          clearInterval(id);
          onActivatedRef.current();
          return;
        }
      } catch {
        /* keep polling */
      }
      if (n >= 20) {
        clearInterval(id);
        setTimedOut(true);
      }
    }, 2000);
    return () => clearInterval(id);
  }, [activating]);

  async function subscribe(planId: string, credits: number, interval: "month" | "year") {
    setErr("");
    setBusyPlan(planId);
    try {
      const { url } = await startCheckout(planId, credits, interval);
      window.location.assign(url); // off to Stripe Checkout
    } catch (e) {
      setErr(humanizeError(e, "We couldn't start checkout. Please try again."));
      setBusyPlan("");
    }
  }

  const useOther = useCallback(async (tenantId: string) => {
    try {
      await switchWorkspace(tenantId);
      window.location.assign("/");
    } catch (e) {
      setErr(humanizeError(e, "Couldn't switch workspace."));
    }
  }, []);

  async function signOut() {
    try {
      await logout();
    } finally {
      onSignedOut();
    }
  }

  async function applyCode() {
    const c = code.trim();
    if (!c || redeeming) return;
    setRedeemErr("");
    setRedeeming(true);
    try {
      await redeemCode(c);
      onActivated(); // plan assigned → workspace active → app unlocks
    } catch (e) {
      setRedeemErr(humanizeError(e, "We couldn't apply that code."));
      setRedeeming(false);
    }
  }

  if (activating) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-6 text-center">
        <Loader2 className="size-8 animate-spin text-primary" />
        <div>
          <h1 className="text-lg font-medium">Activating your subscription…</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            This usually takes a few seconds. You'll be redirected automatically.
          </p>
        </div>
        {timedOut && (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Taking longer than expected. Your payment may still be processing.
            </p>
            <Button variant="outline" onClick={() => window.location.assign("/")}>
              Refresh
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-6xl px-4 py-12">
        <header className="mb-10 text-center">
          <h1 className="text-3xl font-semibold tracking-tight">
            Welcome{current?.name ? ` to ${current.name}` : ""}
          </h1>
          <p className="mx-auto mt-2 max-w-xl text-muted-foreground">
            Choose a plan to activate this workspace. You can change or cancel anytime — upgrades
            apply instantly, downgrades at the end of your billing period.
          </p>
          {returned === "cancelled" && (
            <p className="mt-3 text-sm text-muted-foreground">
              Checkout cancelled — no charge was made. Pick a plan whenever you're ready.
            </p>
          )}
          {!isOwner && (
            <p className="mt-3 text-sm text-muted-foreground">
              Only the workspace owner can choose a plan. Ask them to activate this workspace, or
              switch to one of yours below.
            </p>
          )}
          {err && <p className="mt-3 text-sm text-destructive">{err}</p>}
        </header>

        {plans === null ? (
          <div className="flex justify-center py-20">
            <Spinner />
          </div>
        ) : (
          <>
            {/* Monthly / Annual toggle */}
            <div className="mb-8 flex justify-center">
              <div className="inline-flex items-center rounded-full border bg-muted p-1 text-sm">
                <button
                  type="button"
                  onClick={() => setCycle("month")}
                  className={
                    "rounded-full px-4 py-1.5 font-medium transition-colors " +
                    (cycle === "month" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground")
                  }
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setCycle("year")}
                  className={
                    "flex items-center gap-1.5 rounded-full px-4 py-1.5 font-medium transition-colors " +
                    (cycle === "year" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground")
                  }
                >
                  Annual
                  <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-xs font-semibold text-primary">
                    Save 20%
                  </span>
                </button>
              </div>
            </div>

            <div className="grid gap-5 md:grid-cols-3">
              {plans
                .filter((p) => !p.custom)
                .map((p) => (
                  <PlanCard
                    key={p.id}
                    plan={p}
                    annual={cycle === "year"}
                    busy={busyPlan === p.id}
                    canBuy={isOwner}
                    onSubscribe={subscribe}
                  />
                ))}
            </div>
            {plans.some((p) => p.custom) && <EnterpriseBanner />}
          </>
        )}

        <footer className="mt-12 flex flex-col items-center gap-3 text-sm text-muted-foreground">
          <span>
            Signed in as <span className="text-foreground">{me.email}</span>
          </span>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {isOwner && (
              <Button
                variant="link"
                size="sm"
                className="text-muted-foreground"
                onClick={() => {
                  setRedeemErr("");
                  setRedeemOpen(true);
                }}
              >
                Have a promo code?
              </Button>
            )}
            {others.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm">
                    Switch workspace <ChevronsUpDown className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="w-56">
                  <div className="px-2 py-1 text-xs font-medium text-muted-foreground">
                    Your workspaces
                  </div>
                  {others.map((w) => (
                    <DropdownMenuItem
                      key={w.tenant_id}
                      onSelect={(e) => {
                        e.preventDefault();
                        void useOther(w.tenant_id);
                      }}
                    >
                      <span className="truncate">{w.name || "workspace"}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button variant="ghost" size="sm" onClick={() => void signOut()}>
              <LogOut className="size-4" /> Sign out
            </Button>
          </div>
        </footer>
      </div>

      <Dialog open={redeemOpen} onOpenChange={(o) => !redeeming && setRedeemOpen(o)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Redeem a promo code</DialogTitle>
            <DialogDescription>
              Enter a code to activate this workspace on its plan.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void applyCode();
            }}
            className="space-y-3"
          >
            <Input
              autoFocus
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setRedeemErr("");
              }}
              placeholder="Enter code"
              autoCapitalize="characters"
              className="uppercase"
              aria-invalid={!!redeemErr}
            />
            {redeemErr && <p className="text-sm text-destructive">{redeemErr}</p>}
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setRedeemOpen(false)}
                disabled={redeeming}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!code.trim() || redeeming}>
                {redeeming ? <Spinner /> : null} Apply
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
